import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';

import { useRecordAudio } from 'hooks/audio';

import AudioAnalyser from 'components/AudioAnalyser';
import SimpleAudioPlayer from 'components/SimpleAudioPlayer';

import IconStop from 'icons/icon-stop';
import IconClose from 'icons/icon-close';

import './AudioRecorder.styles.css';

const AudioRecorder = ({ autostart = true, onError, onRecordStart, onComplete, onCancel, provideController }) => {
    const [record, setRecord] = useState(null);
    const { isRecording, stream, start, stop } = useRecordAudio({ onRecorded: setRecord, onError });
    const completeOnReady = useRef(false);

    const audioURL = useMemo(() => {
        if (!record) {
            return null;
        }

        return window.URL.createObjectURL(record.blob);
    }, [record]);

    const handleStopClick = () => {
        stop();
    }

    const handleCancelClick = () => {
        stop();
        onCancel();
    }

    const complete = useCallback(() => {
        if (isRecording || !record) {
            stop();

            completeOnReady.current = true;
            return;
        }

        onComplete(record);
    }, [isRecording, record, onComplete]);

    const handleKeyDown = useCallback(() => {
        complete();
    }, [complete]);

    useEffect(() => {
        const ctrl = {
            complete
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [provideController, complete]);

    useEffect(() => {
        if (isRecording) {
            onRecordStart();
        }
    }, [isRecording, onRecordStart]);

    useEffect(() => {
        if (completeOnReady.current && record) {
            onComplete(record);
        }
    }, [record]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, [handleKeyDown]);

    useEffect(() => {
        if (autostart) {
            start();
        }
    }, []);

    if (!isRecording && !record) {
        return null;
    }

    return (
        <div className="audio-recorder">
            <div className="audio-recorder-main">
                {
                    isRecording && stream ? (
                        <AudioAnalyser stream={stream} />
                    ) : null
                }

                {
                    !isRecording && audioURL ? (
                        <SimpleAudioPlayer src={audioURL} duration={record.details.duration * 1000} />
                    ) : null
                }
            </div>

            <div className="audio-recorder-controls">
                {
                    isRecording ? (
                        <button className="btn btn-secondary btn-xsmall btn-colored btn-icon btn-circle" onClick={handleStopClick}><IconStop size="13px" /></button>
                    ) : null
                }

                <button className="btn btn-secondary btn-xsmall btn-icon btn-circle" onClick={handleCancelClick}><IconClose size="14px" /></button>
            </div>
        </div>
    )
}

export default AudioRecorder;
